// Renders a form for the per-type config object. Falls back to a JSON
// textarea if the type has no UI schema registered (future-proofing).
//
// Props:
//   type        — ingestor type key (e.g. "github")
//   value       — current config value
//   onChange    — fires with the updated value
//
// Note: secrets are rendered by SecretForm, not here. Secrets are
// write-only from the browser (the api never returns them), so this
// form has no "current value" to display.

import { useState } from 'react';
import { getIngestorSchema } from './schema';

export function ConfigForm({
  type,
  value,
  onChange,
}: {
  type: string;
  value: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
}) {
  const schema = getIngestorSchema(type);
  const [useJson, setUseJson] = useState(false);

  if (!schema) {
    return (
      <JsonFallback
        value={value}
        onChange={onChange}
        hint={`No form schema registered for "${type}". Using raw JSON.`}
      />
    );
  }

  if (useJson) {
    return (
      <JsonFallback
        value={value}
        onChange={onChange}
        hint="Edit raw JSON."
        onSwitch={() => setUseJson(false)}
      />
    );
  }

  return (
    <div className="space-y-3">
      {schema.config.map((f) => (
        <div key={f.name} className="space-y-1">
          <label className="label" htmlFor={`cfg-${f.name}`}>
            {f.label}
            {f.required ? <span className="ml-1 text-accent-danger">*</span> : null}
          </label>
          {renderField(`cfg-${f.name}`, f, value[f.name] ?? f.defaultValue ?? '', (v) =>
            onChange({ ...value, [f.name]: v }),
          )}
          {f.help ? <p className="text-xs text-ink-dim">{f.help}</p> : null}
        </div>
      ))}
      <div className="pt-1">
        <button type="button" className="btn" onClick={() => setUseJson(true)}>
          Edit as JSON
        </button>
      </div>
    </div>
  );
}

function renderField(
  id: string,
  field: { name: string; type: string; options?: Array<{ value: string; label: string }>; placeholder?: string },
  value: unknown,
  onChange: (v: unknown) => void,
) {
  if (field.type === 'boolean') {
    return (
      <input
        id={id}
        type="checkbox"
        className="h-4 w-4 rounded border-white/20 bg-bg-elevated"
        checked={Boolean(value)}
        onChange={(e) => onChange(e.target.checked)}
      />
    );
  }
  if (field.type === 'select' && field.options) {
    return (
      <select
        id={id}
        className="input"
        value={String(value ?? '')}
        onChange={(e) => onChange(e.target.value)}
      >
        {field.options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    );
  }
  if (field.type === 'number') {
    return (
      <input
        id={id}
        type="number"
        className="input"
        value={String(value ?? '')}
        placeholder={field.placeholder}
        onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))}
      />
    );
  }
  return (
    <input
      id={id}
      type={field.type === 'url' ? 'url' : 'text'}
      className="input font-mono"
      value={String(value ?? '')}
      placeholder={field.placeholder}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

function JsonFallback({
  value,
  onChange,
  hint,
  onSwitch,
}: {
  value: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
  hint: string;
  onSwitch?: () => void;
}) {
  const [text, setText] = useState(() => JSON.stringify(value, null, 2));
  const [err, setErr] = useState<string | null>(null);
  return (
    <div className="space-y-2">
      <p className="text-xs text-ink-dim">{hint}</p>
      <textarea
        className="input min-h-[8rem] font-mono text-xs"
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          try {
            const parsed = JSON.parse(e.target.value);
            if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
              setErr(null);
              onChange(parsed as Record<string, unknown>);
            } else {
              setErr('Must be a JSON object.');
            }
          } catch (e2) {
            setErr((e2 as Error).message);
          }
        }}
      />
      {err ? <p className="text-xs text-accent-danger" role="alert">{err}</p> : null}
      {onSwitch ? (
        <button type="button" className="btn" onClick={onSwitch}>Back to form</button>
      ) : null}
    </div>
  );
}
